<script setup lang="ts">
import { ref, watch, toRefs } from "vue";
import { useApiClient } from "@/api/client";
import { debounce } from "vue-debounce";
import dayjs from "dayjs";
import ExportScheduleDO = App.Data.ExportScheduler.ExportScheduleDO;
import ExportScheduleRecipientDO = App.Data.ExportScheduler.ExportScheduleRecipientDO;
import { toastApiResult } from "@/toaster";
import useDialog from "@/ui/composables/useDialog";
import NewRecipientDialog from "@/modules/scheduler/components/NewRecipientDialog.vue";
import { useBaseStore } from "../../../stores/base";

const props = defineProps<{
	exportSchedule: ExportScheduleDO;
}>();

const { exportSchedule } = toRefs(props);

const search = ref("");
const itemsPerPage = ref(10);
const items = ref<ExportScheduleRecipientDO[]>([]);
const isLoading = ref(false);
const totalItemCount = ref(0);
const page = ref(1);
const sortBy = ref([]);

const headers = [
	{ title: "Email Address", key: "email", sortable: true },
	{ title: "Added on", key: "created_at", sortable: true },
	{
		title: "Actions",
		key: "actions",
		width: "100",
		sortable: false,
		align: "center",
	},
];

const loadItems = debounce(async ({ page, itemsPerPage, sortBy, search }) => {
	isLoading.value = true;

	const client = useApiClient("exportSchedules");

	const response = await client.getRecipients(
		{ page, itemsPerPage, sortBy, search },
		exportSchedule.value
	);
	if (response.isOk()) {
		items.value = response.value.data;
		totalItemCount.value = response.value.total;
	}

	isLoading.value = false;
}, 300);

const removeRecipient = async (recipient: ExportScheduleRecipientDO) => {
	const confirm = await useDialog().confirm({
		title: "Remove recipient",
		message: `Are you sure you permanently want to remove ${recipient.email} from ${exportSchedule.value.title}?`,
		isDelete: true,
	});
	if (!confirm) return;

	const response = await toastApiResult(
		useApiClient("exportSchedules").deleteRecipient(
			exportSchedule.value,
			recipient
		)
	);
	if (response.isOk()) {
		successCallback();
	}
};

const successCallback = () => {
	loadItems({
		page: page.value,
		itemsPerPage: itemsPerPage.value,
		sortBy: sortBy.value,
		search: search.value,
	});
};

const showNewRecipientDialog = ref(false);

watch(showNewRecipientDialog, (value) => {
	if (!value) {
		successCallback();
	}
});
</script>

<template>
	<new-recipient-dialog
		:allowed-domains="useBaseStore().currentEnvironment.trusted_domains ?? []"
		:export-schedule="exportSchedule"
		v-model="showNewRecipientDialog"
		v-if="showNewRecipientDialog"
	/>
	<div class="pa-5">
		<div class="d-flex mb-5">
			<v-text-field
				v-model="search"
				label="Search"
				class="mr-3"
				prepend-inner-icon="mdi-magnify"
				variant="filled"
				elevation="4"
				hide-details
				single-line
			/>
			<v-btn
				variant="elevated"
				prepend-icon="fa: fa-light fa-plus"
				height="48"
				@click="showNewRecipientDialog = true"
			>
				Add new recipient
			</v-btn>
		</div>
		<v-data-table-server
			v-model:items-per-page="itemsPerPage"
			v-model:page="page"
			v-model:sort-by="sortBy"
			:search="search"
			:headers="headers"
			:items="items"
			:items-length="totalItemCount"
			:loading="isLoading"
			@update:options="loadItems"
		>
			<template v-slot:item.created_at="{ item }">
				{{ dayjs(item.created_at).format("DD MMM YYYY HH:mm") }}
			</template>
			<template v-slot:item.actions="{ item }">
				<v-btn
					size="30"
					icon
					color="primary"
					variant="text"
					@click="removeRecipient(item)"
				>
					<i class="fa-light fa-trash-can text-error"></i
				></v-btn>
			</template>
		</v-data-table-server>
	</div>
</template>

<style scoped lang="scss"></style>
